import { useMeetingEditor } from "./MeetingEditorContext";
import {
  useImmediateMutationSystem,
  type Mutator,
} from "@/lib/queuedMutations";
import { useEffect, useRef, useState } from "react";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { formatNumber } from "@repo/shared/formatNumber";
import {
  type DragData,
  dragInsertPos,
  type DragInsertPos,
  insertClasses,
  isDataForSelf,
  matchingDragType,
} from "./dragDrop";
import {
  AddNew,
  CollapseChevron,
  DragGrip,
  EditPencil,
  PullIn,
  RemoveX,
} from "./iconButtons";
import { useDndContext, useDraggable, useDroppable } from "@dnd-kit/core";
import { type Collapsible, useIndTracker } from "./utilities";
import { cn } from "@repo/shared/utils";
import { multiline } from "@repo/ui/components/utilities/multiline";
import { MeetingEditorAgendaItemLoader } from "./MeetingEditorAgendaItem";
import { useGetAgendaModule } from "@/hooks/queryHooks/agendaModuleHooks";
import {
  agendaModuleRepo,
  type AgendaModuleDto,
} from "@/repositories/agendaModuleRepo";
import type { AgendaItemDto } from "@/repositories/agendaItemRepo";

// #region Module Loader

/** Loaders are necessary to load the object's data and only render the actual component when it
 * finishes loading because useImmediateMutationSystem requires the data be immediately
 * available in the render function. */

export const MeetingEditorAgendaModuleLoader = ({
  ind,
  containerId,
  agendaModuleId,
  onAdd,
  onRemove,
  index,
}: {
  ind: number;
  containerId: string;
  agendaModuleId: string;
  onAdd?: (afterAgendaModuleId: string, load: boolean) => void;
  onRemove?: (agendaModuleId: string) => void | Promise<void>;
  index: number;
}) => {
  const { tenant } = useMeetingEditor();
  const { data: agendaModule } = useGetAgendaModule(tenant.id, agendaModuleId);

  if (!agendaModule) return <p>...</p>;
  if (agendaModule._deleted) return null;

  return (
    <MeetingEditorAgendaModule
      ind={ind}
      containerId={containerId}
      agendaModule={agendaModule}
      onAdd={onAdd}
      onRemove={onRemove}
      index={index}
    />
  );
};

// #endregion

// #region Agenda Module

export const MeetingEditorAgendaModule = ({
  ind,
  containerId,
  agendaModule: _agendaModule,
  onAdd,
  onRemove,
  index,
}: {
  ind: number;
  containerId: string;
  agendaModule: AgendaModuleDto;
  onAdd?: (afterAgendaModuleId: string, load: boolean) => void;
  onRemove?: (agendaModuleId: string) => void | Promise<void>;
  index: number;
}) => {
  const {
    tenant,
    mutationSystem,
    viewOnly,
    openEditForm,
    openLoadForm,
    collapseSet,
    updateAllCollapsed,
    undoStack,
  } = useMeetingEditor();

  const mutationKey = "agendaModule:" + _agendaModule.id;
  const tenantId = tenant.id;
  const agendaModuleMutation: Mutator<AgendaModuleDto> = useFutureCallback(
    async (id, data) => {
      await agendaModuleRepo.update(tenantId, id, data);
    }
  );
  const agendaModule = useImmediateMutationSystem(
    mutationSystem,
    mutationKey,
    _agendaModule
  );

  const canCollapse = !!agendaModule.agendaItemIds.length;
  const [collapsed, _setCollapsed] = useState(false);
  const setCollapsed: typeof _setCollapsed = useFutureCallback((collapsed) => {
    if (canCollapse) {
      _setCollapsed(collapsed);
      updateAllCollapsed();
    }
  });

  // since drag-and-drop is handled by the agenda, pass this function to it via drag-and-drop item data
  const agendaModuleId = agendaModule.id;
  const handleUpdateOrder = useFutureCallback(
    (updater: (agendaItemIds: string[]) => string[]) => {
      const agendaItemIds = updater(agendaModule.agendaItemIds);
      mutationSystem.debounce(
        mutationKey,
        agendaModuleMutation,
        { agendaItemIds },
        agendaModuleId
      );
    }
  );

  const sectionIndex = formatNumber(
    index + 1,
    tenant.numberFormat ?? "decimal"
  );

  // drag-and-drop data
  const containerRef = useRef<HTMLElement | null>(null);

  const dndData: DragData = {
    id: agendaModule.id,
    ind,
    container: containerId,
    type: "agendaModule",
    updateOrder: handleUpdateOrder,
    index,
    overlay: (
      <div
        className="flex flex-row gap-1 py-2"
        style={{
          width: containerRef.current?.offsetWidth,
          height: containerRef.current?.offsetHeight,
        }}
      >
        <DragGrip />
        <div className="flex-grow px-2 rounded-md">
          <div className="flex flex-row min-h-8">
            <p className="text-base font-bold mt-1 mr-2">
              {sectionIndex}. {agendaModule.name}
            </p>
          </div>
        </div>
      </div>
    ),
  };

  const { active, over } = useDndContext();

  const isDraggingSelf = isDataForSelf(active, dndData);
  const isOverSelf = isDataForSelf(over, dndData);
  const insert: DragInsertPos = isOverSelf ? dragInsertPos(active, over) : null;
  const insertInto: DragInsertPos =
    isOverSelf && matchingDragType(active, "agendaItem") ? "after" : null;
  const dndId = dndData.id + ":" + dndData.container + ":" + dndData.ind;

  const {
    attributes,
    listeners,
    setNodeRef: setDraggable,
  } = useDraggable({
    id: dndId,
    data: dndData,
    disabled: viewOnly,
  });

  const { setNodeRef: setDroppable } = useDroppable({
    id: dndId,
    data: dndData,
    disabled:
      viewOnly ||
      !(
        matchingDragType(active, "agendaModule") ||
        matchingDragType(active, "agendaItem")
      ),
  });

  const style = {
    opacity: isDraggingSelf ? 0 : 1,
  };

  const setNodeRef = (ref: HTMLElement | null) => {
    setDraggable(ref);
    setDroppable(ref);
    containerRef.current = ref;
  };

  const handleEdit = useFutureCallback(() =>
    openEditForm("agendaModule", agendaModule)
  );

  const handleRemoveAgendaSection = useFutureCallback(() => {
    onRemove?.(agendaModuleId);
  });

  const handleAddAgendaItem = useFutureCallback(
    async (afterAgendaItemId: string | null, load: boolean) => {
      let newAgendaItem: AgendaItemDto | null = null;
      if (load) {
        newAgendaItem = await openLoadForm<AgendaItemDto>("agendaItem");
      } else {
        newAgendaItem = await openEditForm<AgendaItemDto>("agendaItem", null);
      }

      if (!newAgendaItem) return;

      const prev = agendaModule.agendaItemIds.slice();
      const next = prev.slice();

      if (afterAgendaItemId) {
        const index = prev.indexOf(afterAgendaItemId) + 1;
        next.splice(index, 0, newAgendaItem.id);
      } else {
        next.push(newAgendaItem.id);
      }

      mutationSystem.debounce(
        mutationKey,
        agendaModuleMutation,
        { agendaItemIds: next },
        agendaModuleId
      );

      // push delta to undo stack
      undoStack.push({
        label: load ? "Add Existing Item" : "Add New Item",
        undo() {
          mutationSystem.debounce(
            mutationKey,
            agendaModuleMutation,
            { agendaItemIds: prev },
            agendaModuleId
          );
        },
        redo() {
          mutationSystem.debounce(
            mutationKey,
            agendaModuleMutation,
            { agendaItemIds: next },
            agendaModuleId
          );
        },
      });
    }
  );

  const handleCreateAgendaItem = useFutureCallback(() =>
    handleAddAgendaItem(null, false)
  );

  const handleLoadAgendaItem = useFutureCallback(() =>
    handleAddAgendaItem(null, true)
  );

  const handleCreateSection = useFutureCallback(() => {
    onAdd?.(agendaModuleId, false);
  });

  const handleLoadSection = useFutureCallback(() => {
    onAdd?.(agendaModuleId, true);
  });

  // collapse / expand button
  const toggleCollapsed = useFutureCallback(() => {
    setCollapsed((collapsed) => !collapsed);
  });
  const isCollapsed = useFutureCallback(() => collapsed);
  const getCanCollapse = useFutureCallback(() => canCollapse);

  useEffect(() => {
    const collapsible: Collapsible = {
      id: agendaModuleId,
      type: "agendaModule",
      canCollapse: getCanCollapse,
      isCollapsed,
      setCollapsed,
    };

    collapseSet.add(collapsible);
    return () => {
      collapseSet.delete(collapsible);
    };
  }, [getCanCollapse, isCollapsed, setCollapsed, collapseSet, agendaModuleId]);

  const handleRemoveAgendaItem = useFutureCallback(
    (agendaItemId: string, alsoDelete?: boolean) => {
      const prev = agendaModule.agendaItemIds.slice();
      const next = prev.filter((id) => id !== agendaItemId);

      mutationSystem.debounce(
        mutationKey,
        agendaModuleMutation,
        { agendaItemIds: next },
        agendaModuleId
      );

      if (alsoDelete) return;

      // push delta to undo stack
      undoStack.push({
        label: "Remove Item",
        undo() {
          mutationSystem.debounce(
            mutationKey,
            agendaModuleMutation,
            { agendaItemIds: prev },
            agendaModuleId
          );
        },
        redo() {
          mutationSystem.debounce(
            mutationKey,
            agendaModuleMutation,
            { agendaItemIds: next },
            agendaModuleId
          );
        },
      });
    }
  );

  const { getInd, trackInd } = useIndTracker();

  return (
    <div
      className={cn(
        "dnd relative flex flex-col mb-1 rounded-md bg-white/80",
        insertClasses(insert)
      )}
      style={style}
      ref={setNodeRef}
    >
      <div
        className={cn(
          "relative flex flex-row gap-1 group py-2",
          collapsed && "border-b-2 border-dashed border-gray-200",
          insertClasses(insertInto)
        )}
      >
        <DragGrip disabled={viewOnly} {...attributes} {...listeners} />
        <div className="flex-grow px-2 hover:bg-gray-600/5 rounded-md">
          <div className="flex flex-row min-h-8 gap-x-2">
            <p className="text-base font-bold mt-1 mr-2">
              {sectionIndex}. {agendaModule.name}
            </p>
            {!viewOnly && (
              <>
                <EditPencil title="Edit Agenda Section" onClick={handleEdit} />
                {onRemove && (
                  <RemoveX
                    type="section"
                    title="Remove Section From Agenda"
                    label={agendaModule.name}
                    onClick={handleRemoveAgendaSection}
                  />
                )}
                {onAdd && (
                  <>
                    <AddNew
                      type="agendaModule"
                      title="Create new section below"
                      className="-ml-1"
                      onClick={handleCreateSection}
                    />
                    <PullIn
                      type="agendaModule"
                      title="Add section from template below"
                      className="-ml-1"
                      onClick={handleLoadSection}
                    />
                  </>
                )}
                <AddNew
                  type="agendaItem"
                  title="Create new item for section"
                  onClick={handleCreateAgendaItem}
                />
                <PullIn
                  type="agendaItem"
                  title="Add existing item to section"
                  onClick={handleLoadAgendaItem}
                />
              </>
            )}
            <div className="flex-grow" />
            {canCollapse && (
              <CollapseChevron
                title="Section"
                collapsed={collapsed}
                onClick={toggleCollapsed}
              />
            )}
          </div>
          {agendaModule.description && (
            <p className="text-gray-400 text-sm pb-1 pl-9">
              {multiline(agendaModule.description)}
            </p>
          )}
        </div>
      </div>

      <div className={cn("flex flex-col", collapsed && "hidden")}>
        {agendaModule.agendaItemIds.map((agendaItemId, index) => (
          <MeetingEditorAgendaItemLoader
            key={agendaItemId + ":" + getInd(agendaItemId)}
            ind={trackInd(agendaItemId)}
            containerId={containerId + "/" + ind + "/" + agendaModule.id}
            updateOrder={handleUpdateOrder}
            index={index}
            agendaItemId={agendaItemId}
            onAdd={handleAddAgendaItem}
            onRemove={handleRemoveAgendaItem}
          />
        ))}
      </div>
    </div>
  );
};

// #endregion
